import { loginUser } from 'recoil/loginUser';
import { useRecoilValue } from 'recoil';
import { subjectOptionDataState } from 'recoil/subject/subjectOptionDataState';
import { useQueries } from '@tanstack/react-query';
import { getDataQuery } from 'api';
import { AttendState } from 'components/common/AttendState';
import { useMemo, useState } from 'react';
import OnlineAttendancePopup from 'components/popup/OnlineAttendancePopup';

export default function Attendance() {
  const user = useRecoilValue(loginUser);
  const subjectState = useRecoilValue(subjectOptionDataState);
  const [visiblePopup, setVisiblePopup] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState({ userNumber: '', userLmsNumber: '', userName: '' });

  const studentSubjectApis = useQueries({
    queries: [
      {
        // 학생 - [출결현황] 출결 데이터
        queryKey: ['studentAttendanceList', user, subjectState],
        queryFn: () => getDataQuery(`/attendance/course/${subjectState.courseId}/student/online?studentId=${user.loginId}&openYy=${subjectState.openYy}&openTmGbn=${subjectState.openTmGbn}`),
        enabled: !!subjectState.courseId && !!subjectState.openYy && !!subjectState.openTmGbn,
      },
    ],
  });

  const [studentAttendanceListRaw] = studentSubjectApis?.map((query) => query?.data?.data);

  // 로그인한 학생만 필터링
  const studentAttendanceList = useMemo(() => {
    if (!studentAttendanceListRaw) return null;
    return studentAttendanceListRaw.filter(
      (student) => student.userNumber === user.loginId || student.userLmsNumber === user.loginId
    );
  }, [studentAttendanceListRaw, user.loginId]);

  const handleNameClick = (userNumber, userLmsNumber, userName) => {
    setSelectedStudent({ userNumber, userLmsNumber, userName });
    setVisiblePopup(true);
  };

  return (
    <>
      <div className="d-flex justify-content-start align-items-center mb-3" style={{ gap: '1rem' }}>
        <div className="d-flex align-items-center" style={{ fontSize: '16px' }}>
          <span className="fw-bold me-2">{subjectState.termName}</span>
          <span className="text-muted mx-2">|</span>
          <span className="fw-bold">{subjectState.courseName}</span>
        </div>
      </div>
      {studentAttendanceList ? (
        <>
          <div
            className="table-container"
            style={{ position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div
              id="sticky-header"
              className="p-3"
              style={{
                ...(visiblePopup
                  ? {}
                  : {
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '16px',
                    }),
              }}
            >
              <div>
                <ul className="d-flex justify-content-end">
                  <li className="me-3 d-flex">
                    <p>출석</p>
                    <img
                      src="assets/img/attendance.png"
                      alt="출석"
                      className="after-attendance-blue"
                      style={{ width: '20px', height: '20px', marginLeft: '5px' }}
                    />
                  </li>
                  <li className="me-3 d-flex">
                    <p>미출석</p>
                    <img
                      src="assets/img/absence.png"
                      alt="미출석"
                      className="after-attendance-red"
                      style={{ width: '20px', height: '20px', marginLeft: '5px' }}
                    />
                  </li>
                </ul>
              </div>
            </div>

            {/* 테이블 */}
            <div style={{ overflow: 'auto', flexGrow: '1' }}>
              <table
                style={{
                  width: 'max-content',
                  fontSize: '16px',
                  borderSpacing: '0',
                  borderCollapse: 'separate',
                }}
              >
                <thead
                  className="border border-start-0 border-end-0"
                  style={{ background: '#ebf1fa', border: '2px solid #dee2e6' }}
                >
                  <tr>
                    <td
                      rowSpan={2}
                      className="align-middle CHA_LIGHT border"
                      style={{
                        background: '#EBF1FA',
                        minWidth: '46px',
                        position: 'sticky',
                        top: '0',
                        left: '0',
                        zIndex: '10',
                      }}
                    >
                      <div className="d-flex justify-content-center align-items-center" style={{ fontSize: '14px' }}>
                        번호
                      </div>
                    </td>
                    <td
                      rowSpan={2}
                      className="align-middle CHA_LIGHT border"
                      style={{
                        minWidth: '70px',
                        position: 'sticky',
                        top: '0',
                        left: '46px',
                        zIndex: '10',
                      }}
                    >
                      <div className="d-flex justify-content-center align-items-center" style={{ fontSize: '14px' }}>
                        이름
                      </div>
                    </td>
                    <td
                      rowSpan={2}
                      className="align-middle CHA_LIGHT border"
                      style={{
                        minWidth: '95px',
                        position: 'sticky',
                        top: '0',
                        left: '116px',
                        zIndex: '10',
                      }}
                    >
                      <div className="d-flex justify-content-center align-items-center" style={{ fontSize: '14px' }}>
                        학번
                      </div>
                    </td>
                    <td
                      rowSpan={2}
                      className="align-middle CHA_LIGHT border"
                      style={{ minWidth: '130px', position: 'sticky', top: '0', left: '211px', zIndex: '10' }}
                    >
                      <div
                        className="d-flex justify-content-center align-items-center gap-1"
                        style={{ fontSize: '14px' }}
                      >
                        <div className="d-flex justify-content-center align-items-center gap-1">
                          <span>출석</span>
                          <span>/</span>
                          <span>미출석</span>
                        </div>
                      </div>
                    </td>
                    {/* Module 헤더 */}
                    {studentAttendanceList &&
                      studentAttendanceList[0]?.attendanceSummaryDtoList.map((module) => (
                        <td
                          key={module.moduleNumber}
                          className="border text-center"
                          colSpan={module.canvasAttendanceStates.length}
                          style={{
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            padding: '5px',
                            minWidth: 'auto',
                            position: 'sticky',
                            top: '0',
                            zIndex: '1',
                            background: '#ebf1fa',
                          }}
                        >
                          {module.moduleNumber}주차
                        </td>
                      ))}
                  </tr>
                  <tr>
                    {/* canvasAttendanceStates 길이에 따라 분할 */}
                    {studentAttendanceList &&
                      studentAttendanceList[0]?.attendanceSummaryDtoList.flatMap((module, moduleIndex) =>
                        module.canvasAttendanceStates.map((item, index) => (
                          <td
                            key={`moduleItem-${moduleIndex}-${index}`}
                            className="border text-center"
                            style={{
                              textAlign: 'center',
                              whiteSpace: 'normal',
                              padding: '5px',
                              minWidth: 'auto',
                              position: 'sticky',
                              top: '28px',
                              zIndex: '1',
                              background: '#ebf1fa',
                            }}
                          >
                            <div className="d-flex justify-content-center align-items-center">
                              <span>{index + 1}차시</span>
                            </div>
                          </td>
                        ))
                      )}
                  </tr>
                </thead>
                <tbody>
                  {studentAttendanceList?.map((student, index) => (
                    <tr key={index}>
                      {/* 기본 정보 */}
                      <td className="border" style={{ background: '#EBF1FA', position: 'sticky', left: '0px' }}>
                        <div className="d-flex justify-content-center">{index + 1}</div>
                      </td>
                      <td className="border" style={{ position: 'sticky', left: '46px', background: '#EBF1FA' }}>
                        <div
                          className="d-flex justify-content-center Blue-500 cursor-pointer"
                          onClick={() => handleNameClick(student.userNumber, student.userLmsNumber, student.name)}
                        >
                          {student.name}
                        </div>
                      </td>
                      <td className="border" style={{ position: 'sticky', left: '116px', background: '#EBF1FA' }}>
                        <div className="d-flex justify-content-center">{student.userNumber}</div>
                      </td>
                      <td className="border" style={{ position: 'sticky', left: '211px', background: '#EBF1FA' }}>
                        <div className="d-flex justify-content-center gap-1">
                          <span style={{ color: '#61BF5E' }}>{student.attendanceCnt}</span>
                          <span>/</span>
                          <span style={{ color: '#D92B2B' }}>{student.absenceCnt}</span>
                        </div>
                      </td>

                      {/* Module 상태 */}
                      {student.attendanceSummaryDtoList.flatMap((module) =>
                        module.canvasAttendanceStates.map((item, itemIndex) => (
                          <td key={`state-${module.moduleNumber}-${itemIndex}`} className="border">
                            <div className="d-flex justify-content-center">
                              <AttendState state={item.moduleState} />
                            </div>
                          </td>
                        ))
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        ''
      )}
      {visiblePopup && (
        <OnlineAttendancePopup
          setVisiblePopup={setVisiblePopup}
          userNumber={selectedStudent.userNumber}
          userLmsNumber={selectedStudent.userLmsNumber}
          userName={selectedStudent.userName}
          courseId={subjectState.courseId}
          type={'attendance'}
        />
      )}
    </>
  );
}
